import { z } from "zod";
import { stepTypeKey } from "../../drizzle/stepTypes.js";
import { workflowKey } from "../../workflows/workflow-keys.js";
import { damageAppraisalCheckedDataSchema, damageAppraisalCheckedDataWithMetadataSchema, damageAppraisalDataSchema, damageAppraisalDataWithMetadataSchema, damageAppraisalDocumentsOnlyCheckedDataSchema, damageAppraisalDocumentsOnlyInputSchema, damageAppraisalDocumentsOnlyResultSchema, damageAppraisalExternalRepairReleaseResultSchema, damageAppraisalReleaseResultSchema, damageAppraisalTotalLossResultSchema, } from "../schemas/damage-appraisal.js";
import { linkDocumentsResultSchema } from "../schemas/link-documents.js";
/**
 * Damage Appraisal (Gutachten) — Heidelbot preparation step (system step).
 * Persisted when Heidelbot triggers POST /tasks/email for type damage_appraisal.
 */
export const damageAppraisalHeidelbotPreparationStep = {
    key: stepTypeKey.DAMAGE_APPRAISAL_HEIDELBOT_PREPARATION,
    workflow: workflowKey.DAMAGE_APPRAISAL,
    blocksBucketCompletion: false, // System step — never reached by the banner action
    isSystemStep: true,
    resultSchema: damageAppraisalDataSchema,
};
/**
 * Damage appraisal — user reviews extracted data alongside documents.
 */
export const damageAppraisalCheckStep = {
    key: stepTypeKey.DAMAGE_APPRAISAL_CHECK,
    workflow: workflowKey.DAMAGE_APPRAISAL,
    blocksBucketCompletion: true, // First user step — form result must be submitted via the in-step button, not the banner
    inputSchema: damageAppraisalDataWithMetadataSchema,
    // Produces the full checked data on a decision branch, or the reduced documents-only checked
    // data on the docs-only branch. Full is listed first so a full payload matches it (a reduced
    // payload fails full — missing required expert-report fields — and falls through to the reduced member).
    resultSchema: z.union([damageAppraisalCheckedDataSchema, damageAppraisalDocumentsOnlyCheckedDataSchema]),
    isSystemStep: false,
};
export const damageAppraisalReleaseStep = {
    key: stepTypeKey.DAMAGE_APPRAISAL_RELEASE,
    workflow: workflowKey.DAMAGE_APPRAISAL,
    blocksBucketCompletion: false,
    inputSchema: damageAppraisalCheckedDataWithMetadataSchema,
    resultSchema: damageAppraisalReleaseResultSchema,
    isSystemStep: false,
};
export const damageAppraisalExternalRepairReleaseStep = {
    key: stepTypeKey.DAMAGE_APPRAISAL_EXTERNAL_REPAIR_RELEASE,
    workflow: workflowKey.DAMAGE_APPRAISAL,
    blocksBucketCompletion: false,
    inputSchema: damageAppraisalCheckedDataWithMetadataSchema,
    resultSchema: damageAppraisalExternalRepairReleaseResultSchema,
    isSystemStep: false,
};
export const damageAppraisalTotalLossStep = {
    key: stepTypeKey.DAMAGE_APPRAISAL_TOTAL_LOSS,
    workflow: workflowKey.DAMAGE_APPRAISAL,
    blocksBucketCompletion: false,
    inputSchema: damageAppraisalCheckedDataWithMetadataSchema,
    resultSchema: damageAppraisalTotalLossResultSchema,
    isSystemStep: false,
};
/**
 * Damage appraisal — docs-only follow-up ("Nur Dokumente verlinken"): attaches the
 * email's categorized attachments to the existing ContainerExpertReport without a claim
 * decision, verified values, or a section. Used e.g. when a later email for an already-recorded
 * incident only carries extra documents.
 */
export const damageAppraisalDocumentsOnlyStep = {
    key: stepTypeKey.DAMAGE_APPRAISAL_DOCUMENTS_ONLY,
    workflow: workflowKey.DAMAGE_APPRAISAL,
    blocksBucketCompletion: false,
    inputSchema: damageAppraisalDocumentsOnlyInputSchema,
    resultSchema: damageAppraisalDocumentsOnlyResultSchema,
    isSystemStep: false,
};
/** What document-manager confirmed linked at the check step's transition. Born SUCCESS, one per task. */
export const damageAppraisalLinkDocumentsStep = {
    key: stepTypeKey.DAMAGE_APPRAISAL_LINK_DOCUMENTS,
    workflow: workflowKey.DAMAGE_APPRAISAL,
    blocksBucketCompletion: false,
    resultSchema: linkDocumentsResultSchema,
    isSystemStep: true,
};
export const damageAppraisalSteps = {
    [stepTypeKey.DAMAGE_APPRAISAL_HEIDELBOT_PREPARATION]: damageAppraisalHeidelbotPreparationStep,
    [stepTypeKey.DAMAGE_APPRAISAL_CHECK]: damageAppraisalCheckStep,
    [stepTypeKey.DAMAGE_APPRAISAL_RELEASE]: damageAppraisalReleaseStep,
    [stepTypeKey.DAMAGE_APPRAISAL_EXTERNAL_REPAIR_RELEASE]: damageAppraisalExternalRepairReleaseStep,
    [stepTypeKey.DAMAGE_APPRAISAL_TOTAL_LOSS]: damageAppraisalTotalLossStep,
    [stepTypeKey.DAMAGE_APPRAISAL_DOCUMENTS_ONLY]: damageAppraisalDocumentsOnlyStep,
    [stepTypeKey.DAMAGE_APPRAISAL_LINK_DOCUMENTS]: damageAppraisalLinkDocumentsStep,
};
